import api from "../helpers/axios";

class FileService {
  /**
   * Create file records in the backend
   * @param {Array} fileDataArray - Array of file data objects
   * @returns {Promise<Array>} Created file records
   */
  async createFiles(fileDataArray) {
    try {
      const response = await api.post("/files/", fileDataArray);
      return response.data;
    } catch (error) {
      console.error("Error creating files:", error);
      throw error;
    }
  }

  /**
   * Get file details by file ID
   * @param {string} fileId - File UUID
   * @returns {Promise<Object>} File details
   */
  async getFileById(fileId) {
    try {
      const response = await api.get(`/files/${fileId}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching file details:", error);
      throw error;
    }
  }

  /**
   * Get detailed file information by file IDs
   * @param {Array<string>} fileIds - Array of file UUIDs
   * @returns {Promise<Object>} File details response with files array and metadata
   */
  async getFileDetailsByIds(fileIds) {
    try {
      const payload = {
        file_ids: fileIds,
        file_urls: [],
      };
      const response = await api.post("/files/details", payload);
      return response.data;
    } catch (error) {
      console.error("Error fetching file details by IDs:", error);
      throw error;
    }
  }

  /**
   * Get facility file details by file IDs — admin/superadmin only.
   * Uses /files/facility-details which scopes by facility_id.
   * @param {Array<string>} fileIds - Array of file UUIDs
   * @returns {Promise<Object>} File details response with files array and metadata
   */
  async getFacilityFileDetails(fileIds) {
    try {
      const payload = {
        file_ids: fileIds,
        file_urls: [],
      };
      const response = await api.post("/files/facility-details", payload);
      return response.data;
    } catch (error) {
      console.error("Error fetching facility file details by IDs:", error);
      throw error;
    }
  }

  /**
   * Update file description and name
   * @param {string} fileId - File UUID
   * @param {string} description - New description
   * @param {string} name - New name
   * @returns {Promise<Object>} Updated file data
   */
  async updateFileDescription(fileId, description, name) {
    try {
      const payload = {
        description: description.trim(),
        name: name.trim(),
      };
      const response = await api.put(`/files/${fileId}`, payload);
      return response.data;
    } catch (error) {
      console.error("Error updating file description:", error);
      throw error;
    }
  }

  /**
   * Delete a file
   * @param {string} fileId - File UUID
   * @returns {Promise<void>}
   */
  async deleteFile(fileId) {
    try {
      await api.delete(`/files/${fileId}`);
    } catch (error) {
      console.error("Error deleting file:", error);
      throw error;
    }
  }

  /**
   * Transform Azure Function response to file creation format
   * @param {Array} azureResponse - Response from Azure Function
   * @returns {Array} Formatted file data for creation
   */
  transformAzureResponseToFileData(azureResponse) {
    return azureResponse.map((item) => ({
      user_id: item.user_id,
      description: null,
      chat_id: item.chat_id,
      message_id: item.message_id,
      filename: item.filename,
      blob_url: item.blob_url,
      content_type: item.content_type,
      file_size: item.file_size,
      chunks_count: item.chunks_count,
      project_id: item.project_id,
      workspace_id: item.workspace_id,
    }));
  }

  /**
   * Transform file creation response to file relation format
   * @param {Array} fileCreationResponse - Response from file creation API
   * @param {string} collectionId - Collection UUID
   * @returns {Array} Formatted file relation data
   */
  transformToFileRelations(fileCreationResponse, collectionId) {
    return fileCreationResponse.map((file) => ({
      file_id: file.id,
      file_url: file.blob_url,
      workspace_id: null,
      collection_id: collectionId,
      project_id: null,
      chat_id: null,
    }));
  }

  /**
   * Delete a file (soft delete)
   * @param {string} fileId - File UUID
   * @returns {Promise<Object>} Delete response
   */
  async deleteFile(fileId) {
    try {
      const response = await api.delete(`/files/${fileId}`);
      return response.data;
    } catch (error) {
      console.error("Error deleting file:", error);
      throw error;
    }
  }
}

export default new FileService();
